import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { navLinks, siteInfo } from "../../data/mockData";
import AuthModal from "../auth/AuthModal";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    return localStorage.getItem("astron_logged_in") === "true";
  });
  const location = useLocation();
  const timeoutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const handleLogin = () => {
    if (isLoggedIn) {
      // Logout logic
      setIsLoggedIn(false);
      localStorage.removeItem("astron_logged_in");
      localStorage.removeItem("accessToken");
      // Could also call the /logout endpoint here
    } else {
      setIsAuthModalOpen(true);
    }
  };

  const handleLoginSuccess = () => {
    setIsLoggedIn(true);
    localStorage.setItem("astron_logged_in", "true");
  };

  const handleMouseEnter = (label) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setActiveDropdown(label);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 150);
  };

  // Filter nav links based on auth state
  const visibleLinks = navLinks.filter(link => {
    if (link.requiresAuth && !isLoggedIn) return false;
    return true;
  });

  const isHome = location.pathname === "/";
  const navBg = isScrolled || !isHome
    ? "bg-[#1a1a1a]/95 backdrop-blur-md shadow-lg"
    : "bg-transparent";
  const textColor = "text-white";

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${navBg}`}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <img
                src={siteInfo.logo}
                alt={siteInfo.name}
                className="h-10 w-auto object-contain invert"
              />
              <span className={`text-[9px] tracking-[0.25em] uppercase ${textColor} opacity-60`}
                style={{ fontFamily: "'Inter', sans-serif" }}>
                {siteInfo.tagline}
              </span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-1">
              {visibleLinks.map((link) => (
                <div
                  key={link.label}
                  className="relative"
                  onMouseEnter={() => link.children && handleMouseEnter(link.label)}
                  onMouseLeave={handleMouseLeave}
                >
                  {link.children ? (
                    <button
                      className={`flex items-center gap-1 px-4 py-2 text-[13px] tracking-[0.15em] uppercase ${textColor} hover:text-[#c8a96e] transition-colors duration-300`}
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {link.label}
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${activeDropdown === link.label ? 'rotate-180' : ''}`} />
                    </button>
                  ) : (
                    <Link
                      to={link.path}
                      className={`px-4 py-2 text-[13px] tracking-[0.15em] uppercase ${textColor} hover:text-[#c8a96e] transition-colors duration-300`}
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {link.label}
                    </Link>
                  )}

                  {/* Dropdown */}
                  <AnimatePresence>
                    {link.children && activeDropdown === link.label && (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 mt-0 bg-[#1a1a1a]/95 backdrop-blur-md min-w-[220px] py-2 border border-white/10"
                        onMouseEnter={() => handleMouseEnter(link.label)}
                        onMouseLeave={handleMouseLeave}
                      >
                        {link.children.map((child) => (
                          <Link
                            key={child.label}
                            to={child.path}
                            className="block px-5 py-3 text-[12px] tracking-[0.12em] uppercase text-white/80 hover:text-[#c8a96e] hover:bg-white/5 transition-all duration-300"
                            style={{ fontFamily: "'Inter', sans-serif" }}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>

            {/* Login Button */}
            <div className="hidden lg:block">
              <button
                onClick={handleLogin}
                className={`px-6 py-2 text-[12px] tracking-[0.2em] uppercase border transition-all duration-300 ${
                  isLoggedIn
                    ? "border-[#c8a96e] text-[#c8a96e] hover:bg-[#c8a96e] hover:text-[#1a1a1a]"
                    : `${textColor} border-white/40 hover:border-[#c8a96e] hover:text-[#c8a96e]`
                }`}
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {isLoggedIn ? "Logout" : "Login"}
              </button>
            </div>

            {/* Mobile Toggle */}
            <button
              className="lg:hidden p-2"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? (
                <X className={`w-6 h-6 ${textColor}`} />
              ) : (
                <Menu className={`w-6 h-6 ${textColor}`} />
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="fixed inset-0 z-[60] bg-[#1a1a1a] overflow-y-auto"
          >
            <div className="px-6 py-5 flex items-center justify-between border-b border-white/10">
              <Link to="/" className="flex items-center gap-2">
                <img
                  src={siteInfo.logo}
                  alt={siteInfo.name}
                  className="h-8 w-auto object-contain invert"
                />
              </Link>
              <button onClick={() => setMobileOpen(false)}>
                <X className="w-6 h-6 text-white" />
              </button>
            </div>
            <div className="px-6 py-8">
              {visibleLinks.map((link) => (
                <div key={link.label} className="mb-4">
                  {link.children ? (
                    <>
                      <button
                        onClick={() => setActiveDropdown(activeDropdown === link.label ? null : link.label)}
                        className="flex items-center justify-between w-full py-3 text-[14px] tracking-[0.15em] uppercase text-white/90 border-b border-white/10"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        {link.label}
                        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${activeDropdown === link.label ? 'rotate-180' : ''}`} />
                      </button>
                      <AnimatePresence>
                        {activeDropdown === link.label && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            {link.children.map((child) => (
                              <Link
                                key={child.label}
                                to={child.path}
                                className="block pl-4 py-2.5 text-[12px] tracking-[0.12em] uppercase text-white/60 hover:text-[#c8a96e] transition-colors"
                                style={{ fontFamily: "'Inter', sans-serif" }}
                              >
                                {child.label}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  ) : (
                    <Link
                      to={link.path}
                      className="block py-3 text-[14px] tracking-[0.15em] uppercase text-white/90 border-b border-white/10 hover:text-[#c8a96e] transition-colors"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {link.label}
                    </Link>
                  )}
                </div>
              ))}
              <button
                onClick={handleLogin}
                className={`mt-6 w-full py-3 text-[12px] tracking-[0.2em] uppercase border transition-all duration-300 ${
                  isLoggedIn
                    ? "text-[#c8a96e] border-[#c8a96e]"
                    : "text-white border-white/40 hover:border-[#c8a96e] hover:text-[#c8a96e]"
                }`}
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {isLoggedIn ? "Logout" : "Login"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <AuthModal 
        isOpen={isAuthModalOpen} 
        onClose={() => setIsAuthModalOpen(false)}
        onLoginSuccess={handleLoginSuccess}
      />
    </>
  );
};

export default Navbar;
